import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, Bell, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../context/UserContext';
import { useNotifications } from '../../context/NotificationContext';

const HeaderContainer = styled.header`
  height: 64px;
  background: ${({ theme }) => theme.isDarkMode ? '#101113' : '#FBFBFB'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  position: fixed;
  top: var(--polka-safe-top, 0px);
  left: calc(var(--vk-offset, 0px) + 280px);
  right: var(--vk-offset, 0px);
  z-index: 100;
  transition: background-color 0.3s ease;

  @media (max-width: 1024px) {
    left: 80px;
  }

  @media (max-width: 768px) {
    left: 0;
    padding: 0 16px;
  }

  html[data-telegram='true'] & {
    position: relative;
    top: auto;
    left: auto;
    right: auto;
    flex-shrink: 0;
    width: 100%;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-left: 36px;

  @media (max-width: 768px) {
    padding-left: 0;
    gap: 8px;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  padding: 8px;
  border-radius: 8px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#EFF3F7'};
  border-radius: 20px;
  padding: 8px 16px;
  min-width: 420px;
  transition: background-color 0.3s ease;

  @media (max-width: 1200px) {
    min-width: 320px;
  }

  @media (max-width: 1024px) {
    min-width: 280px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchIcon = styled(Search)`
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : '#65676b'};
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  border: none;
  background: none;
  font-size: 15px;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};
  width: 100%;
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : '#65676b'};
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const DateTime = styled.div`
  text-align: right;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const Time = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};
`;

const DateText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
`;

const IconButtonNoHover = styled.button`
  background: none;
  padding: 8px;
  border-radius: 50%;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
  }

  @media (max-width: 768px) {
    svg {
      width: 26px;
      height: 26px;
    }
  }
`;

const AvatarLink = styled(Link)`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  display: block;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.03);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MobileAvatarLink = styled(Link)`
  display: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.03);
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileAvatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  background: ${({ theme }) => theme.isDarkMode ? '#ffffff' : '#1c1e21'};
  color: ${({ theme }) => theme.isDarkMode ? '#101113' : '#ffffff'};
  font-size: 11px;
  font-weight: 600;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Header = ({ onMenuClick }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useUser();
  const { getUnreadCount } = useNotifications();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const unreadCount = getUnreadCount();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const formatTime = (date) => {
    const locale = i18n.language === 'en' ? 'en-US' : 'ru-RU';
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    const locale = i18n.language === 'en' ? 'en-US' : 'ru-RU';
    return date.toLocaleDateString(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <HeaderContainer>
      <LeftSection>
        <MenuButton onClick={onMenuClick}>
          <Menu size={24} />
        </MenuButton>

        {user && (
          <MobileAvatarLink to="/profile" aria-label={t('header.goToProfile')}>
            <MobileAvatar
              src={user.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'}
              alt={user.name || t('nav.profile')}
            />
          </MobileAvatarLink>
        )}

        <SearchBar>
          <SearchIcon size={18} />
          <SearchInput 
            type="text" 
            placeholder={t('header.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </SearchBar>
      </LeftSection>

      <RightSection>
        <IconButtonNoHover as={Link} to="/notifications">
          <Bell size={22} />
          {unreadCount > 0 && <NotificationBadge>{unreadCount}</NotificationBadge>}
        </IconButtonNoHover>

        {user && (
          <AvatarLink to="/profile" aria-label={t('header.goToProfile')}>
            <AvatarImg
              src={user.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'}
              alt={user.name || t('nav.profile')}
            />
          </AvatarLink>
        )}

        <DateTime>
          <Time>{formatTime(currentTime)}</Time>
          <DateText>{formatDate(currentTime)}</DateText>
        </DateTime>

      </RightSection>
    </HeaderContainer>
  );
};

export default Header;
